import React, { useMemo } from "react";
import { FlatList, SafeAreaView, StyleSheet, Text, View } from "react-native";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import Header from "@/components/ui/Header";
import { ActionButton } from "@/components/ActionButton";
import FeedItemRenderer from "@/components/dashboard/FeedItemRenderer";
import { useFeedData } from "@/hooks/useFeedData";
import { router, useLocalSearchParams } from "expo-router";

const MAX_CLAIMS_TO_SHOW = 5;

const CancelClaimsScreen = () => {
  const params = useLocalSearchParams<{
    reason: string;
    feedback: string;
  }>();

  const { listFeedToRender, claimedClassActions, expiredClassActions } =
    useFeedData();

  // Combine all feed data and take first few items to show
  const claimsToShow = useMemo(() => {
    const allFeedData = [
      ...listFeedToRender,
      ...claimedClassActions,
      ...expiredClassActions,
    ];
    return allFeedData.slice(0, MAX_CLAIMS_TO_SHOW);
  }, [listFeedToRender, claimedClassActions, expiredClassActions]);

  const handleKeepSparrow = () => {
    router.dismissAll();
    router.navigate("/(tabs)/account");
  };

  const handleCancelAnyway = () => {
    router.push({
      pathname: "/account/cancel/final",
      params: {
        reason: params.reason || "",
        feedback: params.feedback || "",
      },
    });
  };

  const handleClaim = (item: any) => {
    router.dismissAll();
    router.navigate("/(tabs)/feeds");
  };

  const renderItem = ({ item }: { item: any }) => (
    <FeedItemRenderer item={item} onClaim={handleClaim} />
  );

  return (
    <SafeAreaView style={styles.container}>
      <Header title="" showBackButton={true} />

      <View style={styles.content}>
        <Text style={styles.title}>
          If you cancel today, you&apos;ll miss out on claims like these:
        </Text>

        <FlatList
          data={claimsToShow}
          renderItem={renderItem}
          keyExtractor={(item) =>
            item?.id || item?._id || Math.random().toString()
          }
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.claimsList}
          ListEmptyComponent={
            <Text style={styles.emptyText}>No claims available</Text>
          }
        />
      </View>

      <View style={styles.bottomSection}>
        <View style={styles.buttonsRow}>
          <ActionButton
            title="Cancel anyway"
            type="secondary"
            onPress={handleCancelAnyway}
            style={styles.actionButton}
          />
          <ActionButton
            title="I'll keep Sparrow"
            type="primary"
            onPress={handleKeepSparrow}
            style={styles.actionButton}
          />
        </View>
      </View>
    </SafeAreaView>
  );
};

export default CancelClaimsScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  title: {
    fontSize: 28,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    marginBottom: 24,
    lineHeight: 36,
    textAlign: "center",
  },
  claimsList: {
    gap: 12,
    paddingBottom: 16,
  },
  emptyText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
    textAlign: "center",
    marginTop: 24,
  },
  bottomSection: {
    paddingHorizontal: 16,
    paddingBottom: 34,
    paddingTop: 12,
  },
  buttonsRow: {
    flexDirection: "row",
    gap: 12,
  },
  actionButton: {
    flex: 1,
    height: 58,
  },
});
